import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { logger } from "@bfc-software/app-toolbox";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";
import {
  pageQuery,
  type PageSearch,
} from "@bfc-software/bfc-web-ui/lib/cursor-pagination";

import {
  ActivationStateParam,
  FulfillmentStateParam,
  taskInternalIdParam,
  toTask,
  toTaskPage,
  toTaskPlan,
} from "./wire";
import { hasSearch } from "./task-model";
import { ActivationFilter } from "./filters";
import type { TaskFilters } from "./filters";
import type { QueryLike } from "./async";
import type {
  WireActivationStateParam,
  WireFulfillmentStateParam,
  WirePagination,
} from "./wire";
import type { TaskPage, TaskPlan } from "./types";

/**
 * The Task plans tab's ways to reach its data.
 *
 * The generated client's own hooks (`apiClient()`), so the request shape, the
 * query key and the response type all come from the schema rather than being
 * restated here. What this file adds is the two things the schema cannot know:
 * which endpoint answers for a given card, and the mapping into view types -
 * done in `select`, so no component ever holds a wire shape and no `useMemo` in a
 * route is doing the mapping.
 *
 * Every hook takes the `orgId` (the Stytch org id, not the URL's stub) as well as
 * the warehouse, because both are in the request. `orgId` is resolved from the
 * auth session, so it can be absent for the first render or two while a session
 * is being minted; reading with no org would hit `/undefined/task-plans`, so the
 * queries wait on it instead. `isPending` while disabled is the same "loading"
 * the screen already draws.
 */

/**
 * Cards per page.
 *
 * Held here rather than shared with the All tasks tab, because a card is not a
 * row: it carries a progress meter, a route list and an expansion, so the number
 * that reads as one screenful is this tab's own question. Not user-adjustable,
 * for the reason the other tab states - the limit is part of what a page means,
 * and one in the URL would resize pages under the reader mid-walk.
 */
const PAGE_SIZE = 10;

/** The lookups' limit: as much as one request can say, which is the cap. */
const LOOKUP_LIMIT = 100;

/**
 * The chip row, as the listing's params.
 *
 * `activationState` filters on the plan's activation *history*, not on its
 * current state, and a plan comes back once per activation matching it - but only
 * `failed` accumulates: a successful activation is capped at one per plan by the
 * logical-route claim, so `completed` never repeats and `none` selects plans
 * with no activation row at all. Asking only for those two is what keeps this a
 * server-side filter without duplicate cards.
 *
 * Which is also why three of the five values are unreachable from the chip row. A
 * queued or processing activation reads as "Activating" on the card and a failed
 * one as "Activation failed"; neither sits on either side of "is this plan on the
 * floor", so both are reachable only from the unfiltered chip.
 *
 * `fulfillmentState` is the second param, and the reason the released half of the
 * row is three chips rather than one. Every released plan holds a `completed`
 * *activation* - that status means the worker finished minting the task rows, and
 * it never changes again - so what tells one released plan from another is
 * execution, read off the activation's `execution_started_at`/
 * `execution_completed_at` stamps. Each of the param's three values is one chip,
 * and the pairing is what makes each chip a single request.
 *
 * `ACTIVATED` is not a chip here, and it is the reason the enum still has one:
 * it means the whole release, which is `not_started` ∪ `in_progress` ∪
 * `completed`, and the param takes one value. A screen wanting the union asks
 * `activationState` alone - which is what this returns for it, unreachable from
 * this tab but not a hole in the switch.
 *
 * Never paired with `activationState: none`, which the service rejects with a 400
 * (`invalid_filter_combination`) - a plan with no activation row has no execution
 * state to narrow. The `NOT_ACTIVATED` chip sends no fulfillment param at all.
 *
 * Both returns are annotated with the generated param types rather than with
 * literals, which is what holds the named members below to the schema.
 */
export function activationQuery(filter: ActivationFilter): {
  activationState?: WireActivationStateParam;
  fulfillmentState?: WireFulfillmentStateParam;
} {
  switch (filter) {
    case ActivationFilter.NOT_ACTIVATED:
      return { activationState: ActivationStateParam.none };
    case ActivationFilter.ACTIVATED:
      return { activationState: ActivationStateParam.completed };
    case ActivationFilter.NOT_STARTED:
      return {
        activationState: ActivationStateParam.completed,
        fulfillmentState: FulfillmentStateParam.not_started,
      };
    case ActivationFilter.IN_PROGRESS:
      return {
        activationState: ActivationStateParam.completed,
        fulfillmentState: FulfillmentStateParam.in_progress,
      };
    case ActivationFilter.COMPLETED:
      return {
        activationState: ActivationStateParam.completed,
        fulfillmentState: FulfillmentStateParam.completed,
      };
    case ActivationFilter.ALL:
      return {};
  }
}

/**
 * The plan listing's query params, filters included.
 *
 * Every control on this tab narrows on the wire rather than over the response.
 * The route box is `routeCode`, a substring match. The task box cannot be a plan
 * param at all - a plan listing knows nothing about task ids - so it arrives here
 * already resolved to the plans holding those tasks, as `planIds`. The chip row
 * is `activationQuery`'s pair.
 *
 * `activation` is not optional, and the default is not applied here: an absent
 * `activation` param means `DEFAULT_ACTIVATION_FILTER`, and that is a fact about
 * the URL, so the route resolves it once alongside the chip it draws as selected.
 * A `?? DEFAULT` in here as well would be a second place that decides what the
 * tab opens on, and the two would eventually disagree.
 *
 * Pure, and exported, because the request shape is the part worth testing: a
 * filter that quietly fails to reach the query reads on screen exactly like a
 * filter that matched everything.
 *
 * A search in either box widens past the chip entirely (`hasSearch`), which is
 * the same rule the All tasks tab applies to its own chips and for the same
 * stated reason: a plan found nowhere because it sat in a scope nobody was
 * looking at is indistinguishable from a plan that does not exist. The route box
 * has the plain form of it: route 20487's plan is findable only if its chip
 * happens to be the selected one, so a search there has to widen past whichever
 * chip is selected or a manager could type a real route code and be told nothing
 * matched. The task box's own first hop (`taskPlanLookupInit`, §5.H) now reads
 * `/tasks` rather than the activation listing, so it can name a plan whatever its
 * activation state - but the widening rule still has to hold for it too: a task
 * search resolves to a set of plan ids and hands them to this listing as
 * `planIds`, and intersecting that with a chip a manager did not ask for would
 * silently drop a real match the moment the two disagreed about scope.
 *
 * Widening rather than clearing the chip, so the chip a manager chose is still
 * there when they empty the box. The route says so on screen while it is in
 * force - `tasks.search.override`, the same note the other tab draws.
 *
 * The cursor pair rides on the same object, because a page is part of the
 * request rather than something applied to its answer: `pageQuery` maps the one
 * URL token onto whichever of `cursor`/`before` its direction names, and
 * contributes nothing at all on page 1.
 */
export function plansQuery({
  warehouseId,
  filters,
  planIds,
  activation,
  page,
}: {
  warehouseId: string;
  filters: TaskFilters;
  planIds?: ReadonlyArray<string> | undefined;
  activation: ActivationFilter;
  page: PageSearch;
}) {
  return {
    warehouseExtId: warehouseId,
    limit: PAGE_SIZE,
    ...(filters.route.kind === "query"
      ? { routeCode: filters.route.text }
      : {}),
    /*
      The selector narrows the CARDS, not just the tables inside an opened one:
      the listing takes `selectorAppUserExtIds` and answers with the plans that
      hold a live assignment to that selector. Same param name and same meaning
      as on the activation listing - the AAA `external_user_id`, not the
      `selector_profile.id` ULID.
    */
    ...(filters.selector.kind === "named"
      ? { selectorAppUserExtIds: [filters.selector.name] }
      : {}),
    ...(planIds ? { planIds: [...planIds] } : {}),
    ...(hasSearch(filters) ? {} : activationQuery(activation)),
    ...pageQuery(page),
  };
}

/**
 * The plans a set of task activations belong to.
 *
 * `task_plan_id` rides on every activation row, so resolving "which plans hold
 * this task" is a flat map and not a second join. Deduplicated because one plan
 * answers for many of its tasks.
 */
export function planIdsOf(
  activations: ReadonlyArray<{ task_plan_id: string }>,
): string[] {
  return [...new Set(activations.map((row) => row.task_plan_id))];
}

/**
 * The requests this tab makes, as their `init` objects.
 *
 * Spelled out once because the query key *is* the init: two hand-written copies
 * of the same query object is how two reads that should share a cache entry stop
 * sharing one.
 */
const plansInit = (
  orgId: string,
  warehouseId: string,
  filters: TaskFilters,
  activation: ActivationFilter,
  page: PageSearch,
  planIds?: ReadonlyArray<string> | undefined,
) => ({
  params: {
    path: { orgId },
    query: plansQuery({ warehouseId, filters, planIds, activation, page }),
  },
});

/**
 * The task box's first hop: which plans hold the task that was typed.
 *
 * `taskInternalIds` is the warehouse-scoped number a manager reads off a sheet,
 * which is exactly what the box collects - so this is an exact-id lookup. Its
 * sibling `taskIds` takes origin-task ULIDs, which is not a thing anybody types.
 *
 * `GET /{orgId}/tasks`, not the activation listing - a plan nobody has released
 * has no `task_activation` row at all, so a lookup that hit that endpoint could
 * never find one, and the task box's search would silently miss every task
 * belonging to an unreleased plan (see §0). `/tasks` answers from the task rows
 * the planning worker wrote, which exist for every plan whatever its activation
 * state, so this lookup now finds a plan whether or not it has been put on the
 * floor.
 *
 * No `live` param - `/tasks` has no such param, because a task row does not go
 * stale the way a `task_activation` row can. And no `sort`, deliberately: this
 * lookup has no `taskPlanId` (it searches across every plan in the warehouse),
 * and `sort_order_asc` without one is a `400 invalid_sort` (§1.C) - `sort_order`
 * is a per-plan sequence and means nothing compared across plans. Omitting the
 * param takes the `generated_at_desc` default, which is fine here: this read is
 * thrown away after `planIdsOf` dedupes it to a set of plan ids, and no cursor
 * from it is ever replayed.
 */
export const taskPlanLookupInit = (
  orgId: string,
  warehouseId: string,
  taskId: number,
) => ({
  params: {
    path: { orgId },
    query: {
      taskInternalIds: [taskId],
      warehouseExtId: warehouseId,
      limit: LOOKUP_LIMIT,
    },
  },
});

/**
 * One open card's tasks, filters included.
 *
 * Modelled on `activationsQuery` in `-data/use-tasks.ts`, and for the same
 * reason: every control on this card narrows on the wire, so the same omission
 * rule applies - an absent param reads as unfiltered, and sending an empty one
 * is a filter that means something the box never typed. This is also the query
 * key, so a card's filter combinations each get their own cache entry rather
 * than fighting over one.
 *
 * Pure, and exported, because the request shape is the part worth testing: a
 * filter that quietly fails to reach the query reads on screen exactly like a
 * filter that matched everything - and on a card, exactly like a plan holding
 * more than the filter bar asked for.
 *
 * Text in the task box that is not a task number at all leaves the param off
 * entirely, which reads as unfiltered - so `usePlanTasks` never asks in that
 * case. Stated there rather than here, because this function describes a
 * request and that case is the absence of one.
 */
export const activationsInit = (
  orgId: string,
  warehouseId: string,
  planId: string,
  filters: TaskFilters,
) => {
  const taskId =
    filters.task.kind === "query"
      ? taskInternalIdParam(filters.task.text)
      : null;

  return {
    params: {
      path: { orgId },
      query: {
        taskPlanId: planId,
        warehouseExtId: warehouseId,
        // Live rows only: what is held now, not the assignment history.
        live: true,
        // An open card has no pager of its own, so this is one request's worth
        // of the plan's tasks - the cap, and stated as a lookup rather than a
        // page.
        limit: LOOKUP_LIMIT,
        ...(taskId === null ? {} : { taskInternalIds: [taskId] }),
        ...(filters.route.kind === "query"
          ? { routeCode: filters.route.text }
          : {}),
        ...(filters.selector.kind === "named"
          ? { selectorAppUserExtIds: [filters.selector.name] }
          : {}),
      },
    },
  };
};

/**
 * One open unactivated card's tasks, filters included.
 *
 * `GET /{orgId}/tasks` rather than the activation listing: that endpoint
 * inner-joins `plan_activation`, so it cannot answer for a plan nobody has
 * pooled for assignment - which is exactly the card this init serves (see §0).
 *
 * No selector param, and none is missing: `TaskSummary` has no holder, because
 * a task nobody released is held by nobody. A named selector is therefore not
 * a narrowing this read can apply but a question it answers empty - see
 * `usePlanTasks`, which does not make this request at all while one is set.
 *
 * `taskPlanId` is always sent - this is one card's tasks - which is what makes
 * `sort: "sort_order_asc"` legal to send unconditionally: that value 400s
 * without a `taskPlanId` (§1.C), and every call site here has one. That is also
 * why this read needs no client-side ordering: the server orders it, and a
 * `select` that re-sorted the page would be exactly the `.filter`-shaped defect
 * this tree does not carry (§3).
 */
export const plannedTasksInit = (
  orgId: string,
  warehouseId: string,
  planId: string,
  filters: TaskFilters,
) => {
  const taskId =
    filters.task.kind === "query"
      ? taskInternalIdParam(filters.task.text)
      : null;

  return {
    params: {
      path: { orgId },
      query: {
        taskPlanId: planId,
        warehouseExtId: warehouseId,
        limit: LOOKUP_LIMIT,
        sort: "sort_order_asc" as const,
        ...(taskId === null ? {} : { taskInternalIds: [taskId] }),
        ...(filters.route.kind === "query"
          ? { routeCode: filters.route.text }
          : {}),
      },
    },
  };
};

/** What one page of this tab is, once the task-id lookup has resolved. */
export interface PlansRead {
  plans: TaskPlan[];
  /** The envelope, straight through, because the pager reads all of it. */
  pagination: WirePagination;
}

export type PlansResult = QueryLike<PlansRead> & { isFetching: boolean };

/**
 * An answer nothing can match, stated here rather than asked of the service.
 *
 * `has_more: false` and nothing else: this listing does not count, so a zeroed
 * `total_count` would be this file claiming a field the service never sends -
 * and absence is what the pager reads as "not reported" (see `ListPager`).
 */
const EMPTY_READ: PlansRead = {
  plans: [],
  pagination: { has_more: false },
};

/**
 * The same answer for one card's tasks: a page holding nothing, and complete.
 *
 * A total of zero rather than an absent one, because this side is the thing
 * that knows: the request was not made, so nothing was truncated and "no tasks"
 * is the whole answer rather than the first hundred of it.
 */
const EMPTY_PAGE: TaskPage = { tasks: [], nextCursor: null, totalCount: 0 };

/**
 * The plans tab's list read.
 *
 * Cards only - no tasks come with it, because `TaskPlanActivationState` already
 * rolls up the counts a collapsed card reports.
 *
 * One page of them. The envelope comes back alongside the cards rather than
 * being dropped in `select`, because the pager is drawn from it - and the plan
 * listing populates only `has_more`/`cursor`, so what the route gets is a
 * forward-only pager until the service reports the counting fields.
 *
 * Every control is applied by the service. The route box is one param and the
 * chip row another (`activationQuery`); the task box is two requests, because no
 * plan param takes a task id: the activation listing says which plans hold the
 * task, and those plan ids are then the plan listing's `planIds` filter. That
 * makes this read dependent - the second request waits on the first - which is
 * why the two results are collapsed into one `QueryLike` here rather than in the
 * route.
 *
 * Nothing is narrowed on this side, which is what makes what is on screen the
 * answer to the request rather than to the request minus whatever the page then
 * hid.
 */
export function useTaskPlans({
  orgId,
  warehouseId,
  filters,
  activation,
  page,
}: {
  orgId: string | null;
  warehouseId: string;
  filters: TaskFilters;
  /** Already defaulted by the route; see `plansQuery`. */
  activation: ActivationFilter;
  page: PageSearch;
}): PlansResult {
  const client = useMemo(() => apiClient(), []);
  const ready = typeof orgId === "string";
  const taskQuery = filters.task.kind === "query" ? filters.task.text : null;
  /**
   * The typed text as the lookup's param, or `null` for text that is not a task
   * number at all - which names no task, and so is answered here rather than
   * asked (see `taskInternalIdParam`).
   */
  const taskId = taskQuery === null ? null : taskInternalIdParam(taskQuery);

  const lookup = client.useQuery(
    "get",
    "/{orgId}/tasks",
    taskPlanLookupInit(orgId ?? "", warehouseId, taskId ?? 0),
    {
      enabled: ready && taskId !== null,
      select: (data) => planIdsOf(data.tasks),
    },
  );

  const planIds = taskId === null ? undefined : lookup.data;
  /**
   * A task id nothing holds narrows to no plans at all.
   *
   * `planIds: []` would serialize to no param and read as "unfiltered" on the
   * wire, handing back every plan - so the request is skipped and the empty
   * answer is stated here instead. Unparseable text lands in the same place.
   */
  const noMatches =
    (taskQuery !== null && taskId === null) ||
    (taskId !== null && planIds?.length === 0);

  const plans = client.useQuery(
    "get",
    "/{orgId}/task-plans",
    plansInit(orgId ?? "", warehouseId, filters, activation, page, planIds),
    {
      enabled:
        ready && !noMatches && (taskId === null || planIds !== undefined),
      select: (data): PlansRead => ({
        plans: data.task_plans.map(toTaskPlan),
        pagination: data.pagination,
      }),
    },
  );

  if (taskQuery !== null) {
    if (lookup.isError) {
      return {
        isPending: false,
        isError: true,
        error: lookup.error,
        data: undefined,
        isFetching: false,
      };
    }
    if (noMatches) {
      return {
        isPending: false,
        isError: false,
        error: null,
        data: EMPTY_READ,
        isFetching: false,
      };
    }
    if (lookup.isPending) {
      return {
        isPending: true,
        isError: false,
        error: null,
        data: undefined,
        isFetching: true,
      };
    }
  }

  return { ...plans, isFetching: plans.isFetching };
}

/**
 * One open plan card's tasks.
 *
 * Two endpoints now, not one: the planning worker writes a plan's task rows
 * once at plan time, so every plan on this tab has tasks to show whatever its
 * activation state (§0) - `plan.activation.kind === "released"` chooses which
 * of the two answers for them, `GET /{orgId}/task-activations` when it is, and
 * `GET /{orgId}/tasks` (`plannedTasksInit`) otherwise. Both `client.useQuery`
 * calls are declared unconditionally, because hooks cannot be conditional, and
 * each is gated by its own `enabled` - the same pattern `useTaskPlans` above
 * uses for its dependent lookup.
 *
 * Only ever called from an expanded card, so the fetch is the expansion rather
 * than something the tab pays for every plan up front. The cache is what makes
 * closing and reopening a card free.
 *
 * Every control on the filter bar narrows this read on the wire. The released
 * arm sends the same three params the All tasks tab does (see `activationsInit`);
 * the planned arm sends the two of those that mean anything for a task nobody
 * released (see `plannedTasksInit`, and `holdsNobody` below for the third).
 * Nothing is filtered on this side: an open card used to re-check the rows it
 * was handed, which was a filter over one truncated page rather than over the
 * plan - so a card could sit under a header claiming four hundred tasks and
 * show whichever handful of them matched inside the first hundred rows.
 */
export function usePlanTasks({
  orgId,
  warehouseId,
  plan,
  filters,
}: {
  orgId: string | null;
  warehouseId: string;
  /** The whole plan, not just its id: which endpoint answers depends on it. */
  plan: TaskPlan;
  filters: TaskFilters;
}): QueryLike<TaskPage> {
  const client = useMemo(() => apiClient(), []);
  const ready = typeof orgId === "string";
  const isReleased = plan.activation.kind === "released";

  /**
   * Text in the task box that is not a task number names no task.
   *
   * The same guard `useTaskList` states, and needed here for the same reason:
   * `taskInternalIds` is an integer set on the wire, so there is nothing to send
   * for "TASK-4005" - and sending nothing reads as unfiltered, which would
   * answer a search that matched nothing with every task in the plan. Applies
   * to both arms alike.
   */
  const isTaskUnaskable =
    filters.task.kind === "query" &&
    taskInternalIdParam(filters.task.text) === null;

  /**
   * A named selector, asked of a plan that has released nothing.
   *
   * `TaskSummary` carries no holder - a task nobody released is held by
   * nobody - so a selector filter is not a narrowing `plannedTasksInit` can
   * apply. It is a question this card answers empty rather than a param to
   * drop: dropping it would read as unfiltered on the wire and hand back the
   * whole plan under a selector search that named nobody in it.
   */
  const holdsNobody = !isReleased && filters.selector.kind === "named";

  const activations = client.useQuery(
    "get",
    "/{orgId}/task-activations",
    activationsInit(orgId ?? "", warehouseId, plan.id, filters),
    {
      enabled: ready && isReleased && !isTaskUnaskable,
      select: (data) =>
        toTaskPage(
          data.pagination,
          data.task_activations.map((row) =>
            toTask({ kind: "activated", row }),
          ),
        ),
    },
  );

  const planned = client.useQuery(
    "get",
    "/{orgId}/tasks",
    plannedTasksInit(orgId ?? "", warehouseId, plan.id, filters),
    {
      enabled: ready && !isReleased && !isTaskUnaskable && !holdsNobody,
      // No ordering here - the server orders it via `sort: "sort_order_asc"`
      // in `plannedTasksInit` (§1.C). `select` maps and nothing else.
      select: (data) =>
        toTaskPage(
          data.pagination,
          data.tasks.map((row) => toTask({ kind: "planned", row })),
        ),
    },
  );

  if (isTaskUnaskable || holdsNobody) {
    return {
      isPending: false,
      isError: false,
      error: null,
      data: EMPTY_PAGE,
    };
  }

  return isReleased ? activations : planned;
}

/**
 * Activate or deactivate a plan.
 *
 * One hook over two endpoints, because the screen offers one decision. On the
 * wire they are not symmetric at all - `activate` mints an activation and
 * answers 202, `reset-activation` clears one and answers 204 - but a manager is
 * pressing "put this wave on the floor" or "take it back off", and a caller that
 * had to know which verb the service uses would be leaking the asymmetry into
 * the card.
 *
 * `reset-activation` 409s while any activation of the plan is in flight
 * (ADR-0019 §3.1.3), which is the exact predicate `PlanActivation.isSettling`
 * carries and the card disables on. A stale page can still get here, and the
 * rejected `ErrorResponse` is what the toast then quotes.
 *
 * Invalidates rather than writing the result into the cache. Activating a plan
 * changes what every task in it reads as - a whole plan's worth of rows move from
 * "not activated" to "unassigned", and they move between two different endpoints
 * while they do it - so patching the one plan would leave the task rows stating
 * the opposite of the card above them. That also rules out an optimistic flip:
 * activation is asynchronous on the service's side (202, then a worker), so the
 * truthful next state is "activating", which only the refetch can see.
 */
/*
  No warehouse: every key this invalidates is either an endpoint prefix or one
  plan's own tasks. A prefix covers every warehouse a manager has visited in this
  session, which is broader than the write and deliberately so - narrowing it
  would leave another warehouse's cached page to answer for a plan that moved.
*/
export function useSetPlanActivation(orgId: string | null) {
  const client = useMemo(() => apiClient(), []);
  const queryClient = useQueryClient();

  const activate = client.useMutation(
    "post",
    "/{orgId}/task-plans/{id}/activate",
  );
  const reset = client.useMutation(
    "post",
    "/{orgId}/task-plans/{id}/reset-activation",
  );

  /**
   * The card list, both of the opened card's possible task reads, and the All
   * tasks tab.
   *
   * Both task reads because the write is exactly what moves the plan between
   * them - `usePlanTasks` reads `/task-activations` when released and `/tasks`
   * otherwise, and activating or deactivating a plan is precisely what flips
   * which of the two answers for it: the one that just answered is now stale
   * and the one about to answer has nothing cached. Missing either key would
   * leave one of the two possible reads holding a page from before the plan
   * moved. The All tasks tab is a separate read of the same floor and is now
   * wrong about every task in the plan; that cross-read invalidation is the
   * price of splitting the reads, and it belongs here at the write rather than
   * in either tab.
   *
   * The three listings are invalidated by key *prefix* rather than by their
   * exact init, because the filter bar is part of the request: there is one
   * cache entry per filter combination a manager has visited, and naming only
   * the current one would leave the others to answer from a cache the write
   * just falsified. A prefix also covers the task-id lookup (`/tasks`, since
   * §5.H), whose answer an activation changes.
   */
  const invalidate = async () => {
    const keys = [
      ["get", "/{orgId}/task-plans"],
      ["get", "/{orgId}/task-activations"],
      ["get", "/{orgId}/tasks"],
    ];

    await Promise.all(
      keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );
  };

  const setActivation = async (planId: string, isActivated: boolean) => {
    const init = { params: { path: { orgId: orgId!, id: planId } } };

    try {
      if (isActivated) {
        // Absent body activates the full seeded warehouse roster. An explicit
        // `selector_ids` array would set the roster to exactly those selectors
        // and `[]` would pool every task; neither is a decision this screen
        // offers yet.
        await activate.mutateAsync(init);
        logger.info("Task plan activation accepted", { planId });
      } else {
        await reset.mutateAsync(init);
        logger.info("Task plan activation reset", { planId });
      }
    } finally {
      // In a `finally` because a failed write is a reason to refetch, not a
      // reason to skip it: the likely failure is a plan someone else moved, and
      // the refetch is what corrects the card the manager is looking at.
      await invalidate();
    }
  };

  return {
    setActivation,
    isPending: activate.isPending || reset.isPending,
    /**
     * Which card is mid-write, so only that one shows a spinner. Read off the
     * pending mutation rather than off whichever ran last - react-query keeps
     * `variables` after a mutation settles, so the plan id outlives the write.
     */
    pendingPlanId: activate.isPending
      ? (activate.variables?.params?.path?.id ?? null)
      : reset.isPending
        ? (reset.variables?.params?.path?.id ?? null)
        : null,
  };
}
